import { Injectable } from '@angular/core';
import { CanvasBranch, CanvasNode, Connection, Position } from './canvas-workflow.interface';
import { AbstractStepDTO } from '@tj/tj-dmp-lib-api';

@Injectable({
  providedIn: 'root'
})
export class ConnectionService {

  // Constantes pour les connexions
  readonly COMPONENT_HEIGHT = 80;

  /**
   * Génère toutes les connexions pour le workflow
   */
  generateConnections(rootBranch: CanvasBranch): Connection[] {
    const connections: Connection[] = [];
    this.generateBranchConnections(rootBranch, connections);
    return connections;
  }

  /**
   * Génération récursive des connexions pour une branche
   */
  private generateBranchConnections(branch: CanvasBranch, connections: Connection[]): void {
    
    if(branch.children.length === 1 && branch.children[0].type === 'main'){
      const connection: Connection = {
        id: `seq-${branch.id}-${branch.children[0].id}`,
        type: 'sequence',
        fromBranchId: branch.id,
        toBranchId: branch.children[0].id,
        points: this.calculateSequenceBranches(branch, branch.children[0])
      }
      connections.push(connection)
    }

    // 2. Connexions conditionnelles vers les branches enfants
    else if (branch.children.length === 2) {
      const conditionNode = this.findConditionNode(branch);

      if (conditionNode) {
        branch.children.forEach(childBranch => {
          const firstChildNode = childBranch.nodes[0];

          if (firstChildNode) {
            const connection: Connection = {
              id: `cond-${conditionNode.id}-${firstChildNode.id}`,
              type: 'condition',
              fromBranchId: branch.id,
              toBranchId: childBranch.id,
              points: this.calculateConditionalBranches(branch, childBranch)
            };

            connections.push(connection);
          }
        });
      }
    }

    // 3. Récursion sur les branches enfants
    branch.children.forEach(childBranch => {
      this.generateBranchConnections(childBranch, connections);
    });
  }

  /**
   * Trouve le nœud de condition dans une branche
   */
  private findConditionNode(branch: CanvasBranch): CanvasNode | null {
    return branch.nodes.find(node => node.step?.type === AbstractStepDTO.TypeEnum.CONDITIONIF) || null;
  }

  /**
   * Calcule les points pour une connexion séquentielle
   */
  private calculateSequenceBranches(fromBranch: CanvasBranch, toBranch: CanvasBranch): Position[] {
    const startX = fromBranch.position.x + (fromBranch.width / 2);
    const startY = fromBranch.position.y + fromBranch.height;
    const startPoint = { x: startX, y: startY };

    // Point d'arrivée : centre horizontal et haut de la branche cible
    const endX = toBranch.position.x + (toBranch.width / 2);
    const endY = toBranch.position.y;
    const endPoint = { x: endX, y: endY };

    return [startPoint,  endPoint];
  }

  /**
   * Calcule les points pour une connexion conditionnelle
   */
  private calculateConditionalBranches(fromBranch: CanvasBranch, toBranch: CanvasBranch): Position[] {
    // Point de départ : centre horizontal et bas de la branche source
    const startX = fromBranch.position.x + (fromBranch.width / 2);
    const startY = fromBranch.position.y + fromBranch.height;
    const startPoint = { x: startX, y: startY };

    // Point d'arrivée : centre horizontal et haut de la branche cible
    const endX = toBranch.position.x + (toBranch.width / 2);
    const endY = toBranch.position.y;
    const endPoint = { x: endX, y: endY };

    // Points intermédiaires pour créer un chemin en escalier
    const midY = startY + 50;
    const midPoint1 = { x: startX, y: midY };
    const midPoint2 = { x: endX, y: midY };

    return [startPoint, midPoint1, midPoint2, endPoint];
}

  /**
   * Génère le path SVG pour une connexion
   */
  getConnectionPath(connection: Connection): string {
    if (connection.points.length < 2) return '';

    let path = `M ${connection.points[0].x} ${connection.points[0].y}`;

    if (connection.type === 'sequence') {
      // Ligne droite pour les connexions séquentielles
      path += ` L ${connection.points[1].x} ${connection.points[1].y}`;
    } else {
      // Lignes avec angles droits pour les connexions conditionnelles
      for (let i = 1; i < connection.points.length; i++) {
        path += ` L ${connection.points[i].x} ${connection.points[i].y}`;
      }
    }

    return path;
  }

  /**
   * Ajoute une flèche à la fin de chaque connexion
   */
  getArrowPath(connection: Connection): string {
    if (connection.points.length < 2) return '';

    const lastPoint = connection.points[connection.points.length - 1];
    const secondLastPoint = connection.points[connection.points.length - 2];

    // Calculer l'angle de la flèche
    const angle = Math.atan2(
      lastPoint.y - secondLastPoint.y,
      lastPoint.x - secondLastPoint.x
    );

    const arrowSize = 8;
    const angle1 = angle + Math.PI / 6;
    const angle2 = angle - Math.PI / 6;

    const x1 = lastPoint.x - arrowSize * Math.cos(angle1);
    const y1 = lastPoint.y - arrowSize * Math.sin(angle1);
    const x2 = lastPoint.x - arrowSize * Math.cos(angle2);
    const y2 = lastPoint.y - arrowSize * Math.sin(angle2);

    return `M ${lastPoint.x} ${lastPoint.y} L ${x1} ${y1} M ${lastPoint.x} ${lastPoint.y} L ${x2} ${y2}`;
  }
}